import { concat } from '@ember/helper';
import { service } from '@ember/service';
import { waitFor } from '@ember/test-waiters';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import { and } from 'ember-truth-helpers';
import LineClamp from 'garaje/components/line-clamp';
import htmlSafe from 'garaje/helpers/html-safe';
import type ApprovalStatusModel from 'garaje/models/approval-status';
import type BlacklistFilter from 'garaje/models/blacklist-filter';
import type Entry from 'garaje/models/entry';
import Invite from 'garaje/models/invite';
import type { BlocklistReport } from 'garaje/models/screener-result';
import ScreenerResult from 'garaje/models/screener-result';
import _countBy from 'lodash/countBy';
import { unique } from 'macro-decorators';

interface ApprovalBlocklistReportSignature {
  Args: {
    approval: ApprovalStatusModel | ScreenerResult<BlocklistReport>;
    model: Invite | Entry;
  };
}

export default class ApprovalBlocklistReport extends Component<ApprovalBlocklistReportSignature> {
  @service declare store: StoreService;

  @tracked blocklistFilter: BlacklistFilter | null = null;

  @unique('blocklistFilterIdsFromReports') blocklistFiltersIdsThatMatched!: string[];

  constructor(owner: unknown, args: ApprovalBlocklistReportSignature['Args']) {
    super(owner, args);
    void this.loadBlocklistFilterTask.perform();
  }

  get invite(): Invite | null {
    if (this.args.model instanceof Invite) {
      return this.args.model;
    }
    return null;
  }

  get blocklistFilterIdsFromReports(): string[] {
    if (this.args.approval instanceof ScreenerResult) {
      return <string[]>(
        this.args.approval.report.matchedFilters
          .map((filter) => filter.filterId)
          .filter((blocklistFilterId) => !!blocklistFilterId)
      );
    }

    return this.args.approval.blocklistReport
      .map((blocklistReport) => blocklistReport.blacklistFilterId!)
      .filter((blocklistFilterId) => !!blocklistFilterId);
  }

  get blocklistFilterReportCounts(): Record<string, number> {
    return _countBy(this.blocklistFilterIdsFromReports);
  }

  get topMatchedFilterId(): string | undefined {
    const counts = this.blocklistFilterReportCounts;
    const sorted = this.blocklistFiltersIdsThatMatched.sort((a, b) => counts[b]! - counts[a]!);
    return sorted[0];
  }

  loadBlocklistFilterTask = task(
    waitFor(async () => {
      if (!this.topMatchedFilterId) return;
      const blocklistFilter = await this.store.findRecord('blacklist-filter', this.topMatchedFilterId);
      this.blocklistFilter = blocklistFilter;
    }),
  );
  <template>
    <div class="pb-1" data-test-blocklist-details>
      Matches
      {{#if (and this.blocklistFilter.photo.thumb this.blocklistFilter.fullName)}}
        <div
          data-test-blocklist-filter-photo
          class="inline-block mx-1 bg-center bg-cover rounded-full"
          style={{htmlSafe
            (concat
              "width:20px; height:20px; margin-bottom:-4px; background-image:url(" this.blocklistFilter.photo.thumb ");"
            )
          }}
        />
      {{/if}}
      {{#if (and @model.needsPropertyApprovalReview this.invite)}}
        property block list. You may contact your property team.
      {{else if this.blocklistFilter.fullName}}
        <span class="font-bold break-words break-all" data-test-blocklist-filter-name>
          {{this.blocklistFilter.fullName}}
        </span>
        on your block list.
      {{else}}
        to your block list.
      {{/if}}
    </div>
    {{#if this.blocklistFilter.reason}}
      <div class="text-xs text-carbon-50 break-words break-all" data-test-blocklist-filter-reason>
        <LineClamp @lines={{2}} @text={{concat "Reason for blocking: " this.blocklistFilter.reason}} />
      </div>
    {{/if}}
  </template>
}
